import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { getApiUrl } from '../useAuth/authUtils'

/**
 * Unified API Fetch Wrapper
 *
 * Provides consistent token management across all REST API calls,
 * following the same pattern as GraphQL authentication.
 *
 * Key Features:
 * - Dynamic token acquisition per request (no global interceptors)
 * - Automatic 401 retry with fresh token
 * - Promise-based token acquisition to prevent race conditions
 * - Clean error handling without complex interceptor logic
 */

interface ApiFetchConfig extends Omit<AxiosRequestConfig, 'headers'> {
  headers?: Record<string, string>
  scopes?: string[]
  fullResponse?: boolean // If true, return full AxiosResponse instead of just data
}

/**
 * Get fresh token for API request
 * @param scopes - Optional scopes for MSAL
 * @returns Promise<string> - Bearer token
 */
async function getAuthToken(scopes?: string[]): Promise<string> {
  const { getTokenService, initializeMsal } = await import('../Provider/MsalAuthProvider')

  await initializeMsal

  const tokenService = getTokenService()

  const defaultScopes = [
    `api://${(window as any).REACT_APP_AZURE_AD_SCOPE}.pepsico.onmicrosoft.com/${(window as any).REACT_APP_CLIENTID_AZURE_AD}/Settings`,
  ]

  return tokenService.getToken(scopes || defaultScopes)
}

/**
 * Unified API fetch wrapper with automatic token management
 *
 * @param config - Axios request configuration
 * @returns Promise<T> - Response data (or full AxiosResponse if fullResponse=true)
 */
export async function apiFetch<T = any>(config: ApiFetchConfig): Promise<T> {
  try {
    // Get fresh token for this request
    const token = await getAuthToken(config.scopes)

    // Prepare request with auth header
    const requestConfig: AxiosRequestConfig = {
      ...config,
      baseURL: config.baseURL || getApiUrl(),
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    }

    // Remove scopes from config (not part of Axios config)
    delete (requestConfig as any).scopes

    console.log(
      `[apiFetch] Making ${requestConfig.method?.toUpperCase()} request to ${requestConfig.url}`
    )

    const response: AxiosResponse<T> = await axios.request(requestConfig)

    // Return full response if requested, otherwise just data
    return config.fullResponse ? (response as any) : response.data
  } catch (error: any) {
    // Handle 401 errors with automatic retry
    if (error.response?.status === 401) {
      console.log('[apiFetch] 401 error detected, attempting token refresh and retry')

      try {
        // Get fresh token and retry once
        const freshToken = await getAuthToken(config.scopes)

        const retryConfig: AxiosRequestConfig = {
          ...config,
          baseURL: config.baseURL || getApiUrl(),
          headers: {
            ...config.headers,
            Authorization: `Bearer ${freshToken}`,
          },
        }

        // Remove scopes from config
        delete (retryConfig as any).scopes

        console.log(
          `[apiFetch] Retrying ${retryConfig.method?.toUpperCase()} request with fresh token`
        )

        const retryResponse: AxiosResponse<T> = await axios.request(retryConfig)

        // Return full response if requested, otherwise just data
        return config.fullResponse ? (retryResponse as any) : retryResponse.data
      } catch (retryError) {
        console.error('[apiFetch] Token refresh and retry failed:', retryError)
        throw retryError
      }
    }

    // Re-throw non-401 errors
    throw error
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const apiGet = <T = any>(
  url: string,
  config: Omit<ApiFetchConfig, 'method' | 'url'> = {}
): Promise<T> => apiFetch<T>({ ...config, method: 'GET', url })

export const apiPost = <T = any>(
  url: string,
  data?: any,
  config: Omit<ApiFetchConfig, 'method' | 'url' | 'data'> = {}
): Promise<T> => apiFetch<T>({ ...config, method: 'POST', url, data })

export const apiPut = <T = any>(
  url: string,
  data?: any,
  config: Omit<ApiFetchConfig, 'method' | 'url' | 'data'> = {}
): Promise<T> => apiFetch<T>({ ...config, method: 'PUT', url, data })

export const apiPatch = <T = any>(
  url: string,
  data?: any,
  config: Omit<ApiFetchConfig, 'method' | 'url' | 'data'> = {}
): Promise<T> => apiFetch<T>({ ...config, method: 'PATCH', url, data })

export const apiDelete = <T = any>(
  url: string,
  config: Omit<ApiFetchConfig, 'method' | 'url'> = {}
): Promise<T> => apiFetch<T>({ ...config, method: 'DELETE', url })
